import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaLock, FaUser, FaStar, FaChartBar } from 'react-icons/fa';
import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import Avatar from '../../components/misc/Avatar';
import formsService from '../../../services/community/formsService';
import { Form, FormQuestionType, FormResponse } from '../../../models/forms.model';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';

export default function FormResponsesPage(): JSX.Element {
  const { t } = useTranslation();
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const community = useSelectedCommunity();

  useEffect(() => {
    if (!formId) return;
    setLoading(true);
    (async () => {
      try {
        const f = await formsService.get(formId);
        setForm(f);
      } catch (e) {
        setForm(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  // Aggregate responses by question for summary
  const questionSummaries = useMemo(() => {
    if (!form) return {};

    const summaries: Record<string | number, any> = {};

    // Initialize summaries per question
    form.questions.forEach((question) => {
      const qId = question.formQuestionId || question.questionId;
      summaries[qId] = { type: question.type, data: {} };

      switch (question.type) {
        case FormQuestionType.CHOICE:
          const optionCounts: Record<string | number, number> = {};
          question.options?.forEach((opt) => {
            const optId = opt.formOptionId || opt.id;
            optionCounts[optId] = 0;
          });
          summaries[qId].data = optionCounts;
          break;

        case FormQuestionType.RATING:
          const scale = question.meta?.scale || 5;
          const counts: Record<number, number> = {};
          for (let i = 1; i <= scale; i += 1) counts[i] = 0;
          summaries[qId].data = { counts, total: 0, sum: 0, scale };
          break;

        case FormQuestionType.NUMBER:
          summaries[qId].data = { values: [] as number[] };
          break;

        case FormQuestionType.DATE:
          summaries[qId].data = { dates: [] as string[] };
          break;

        case FormQuestionType.TEXT:
        default:
          summaries[qId].data = { freq: {} as Record<string, number> };
      }
    });

    // Aggregate answers
    form.responses.forEach((response) => {
      const answers = response.answers || [];
      answers.forEach((answer) => {
        const questionObj = form.questions.find(
          (qq) =>
            qq.formQuestionId === answer.questionId ||
            qq.questionId === answer.questionId ||
            String(qq.questionId) === String(answer.questionId) ||
            String(qq.formQuestionId) === String(answer.questionId)
        );
        if (!questionObj) return;

        const qId = questionObj.formQuestionId || questionObj.questionId;
        const s = summaries[qId];
        if (!s) return;

        switch (questionObj.type) {
          case FormQuestionType.CHOICE:
            if (answer.optionId) {
              s.data[answer.optionId] = (s.data[answer.optionId] || 0) + 1;
            }
            break;

          case FormQuestionType.RATING:
            const rating = Number(answer.text || answer.response || 0);
            if (rating) {
              s.data.counts[rating] = (s.data.counts[rating] || 0) + 1;
              s.data.total = (s.data.total || 0) + 1;
              s.data.sum = (s.data.sum || 0) + rating;
            }
            break;

          case FormQuestionType.NUMBER:
            const num = Number(answer.text || answer.response);
            if (!Number.isNaN(num)) s.data.values.push(num);
            break;

          case FormQuestionType.DATE:
            const dateVal = answer.text || answer.response;
            if (dateVal) s.data.dates.push(dateVal);
            break;

          case FormQuestionType.TEXT:
          default:
            const txt = (answer.text || answer.response || '').trim();
            if (txt) {
              s.data.freq[txt] = (s.data.freq[txt] || 0) + 1;
            }
        }
      });
    });

    // Post-process derived stats
    Object.keys(summaries).forEach((key) => {
      const s = summaries[key];

      if (s.type === FormQuestionType.RATING) {
        const total = s.data.total || 0;
        s.data.average = total > 0 ? s.data.sum / total : 0;
      } else if (s.type === FormQuestionType.NUMBER) {
        const v = s.data.values as number[];
        s.data.count = v.length;
        s.data.average = v.length > 0 ? v.reduce((a: number, b: number) => a + b, 0) / v.length : 0;
        s.data.min = v.length > 0 ? Math.min(...v) : null;
        s.data.max = v.length > 0 ? Math.max(...v) : null;
      } else if (s.type === FormQuestionType.DATE) {
        const dates = s.data.dates as string[];
        if (dates.length > 0) {
          const sorted = dates.map((d) => new Date(d)).sort((a, b) => a.getTime() - b.getTime());
          s.data.earliest = sorted[0].toISOString();
          s.data.latest = sorted[sorted.length - 1].toISOString();
        } else {
          s.data.earliest = null;
          s.data.latest = null;
        }
      } else if (s.type === FormQuestionType.TEXT) {
        const freq = s.data.freq as Record<string, number>;
        s.data.top = Object.entries(freq)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([text, count]) => ({ text, count }));
      }
    });

    return summaries;
  }, [form]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAnswerDisplay = (response: FormResponse, questionId: string | number) => {
    const answers = (response.answers || []).filter(
      (a) =>
        a.questionId === questionId ||
        String(a.questionId) === String(questionId)
    );
    const question = form?.questions.find(
      (q) =>
        q.formQuestionId === questionId ||
        q.questionId === questionId ||
        String(q.questionId) === String(questionId) ||
        String(q.formQuestionId) === String(questionId)
    );

    if (!question || answers.length === 0) return '-';

    switch (question.type) {
      case FormQuestionType.CHOICE:
        const selectedOptions = answers
          .map((a) =>
            question.options?.find(
              (o) =>
                o.formOptionId === a.optionId ||
                o.id === a.optionId ||
                String(o.id) === String(a.optionId) ||
                String(o.formOptionId) === String(a.optionId)
            )?.label
          )
          .filter(Boolean);
        return selectedOptions.join(', ') || '-';

      case FormQuestionType.RATING:
        const rating = Number(answers[0]?.text || answers[0]?.response || 0);
        const scale = question.meta?.scale || 5;
        return (
          <div className="flex items-center gap-1">
            {Array.from({ length: scale }, (_, i) => (
              <FaStar
                key={i}
                className={`h-4 w-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
              />
            ))}
            <span className="ml-2 text-sm text-gray-600">
              ({rating}/{scale})
            </span>
          </div>
        );

      case FormQuestionType.DATE:
        const dateValue = answers[0]?.text || answers[0]?.response;
        return dateValue
          ? new Date(dateValue).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '-';

      default:
        return answers[0]?.text || answers[0]?.response || '-';
    }
  };

  const handleBack = () => {
    navigate('/forms');
  };

  if (!formId) return <></>;

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-sm text-primary-700">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-primary-700">{t('general.loading')}</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-sm text-primary-700">{t('page.forms.notFound')}</p>
        <Button variant="primary" onClick={handleBack}>
          {t('general.back')}
        </Button>
      </div>
    );
  }

  // Check if results are private
  if (form.responsesHidden) {
    return (
      <div className="flex flex-col gap-6 items-center">
        <PageHeader title={t('page.forms.results.title', { title: '' })} />

        <div className="flex flex-col gap-6 w-full max-w-7xl px-6">
          <Button variant="tertiary" onClick={handleBack}>
            <div className="flex items-center gap-2">
              <FaArrowLeft className="h-4 w-4" />
              {t('general.back')}
            </div>
          </Button>

          <div className="flex flex-col items-center justify-center gap-4 rounded-[12px] bg-secondary-200/70 p-12 w-full max-w-5xl mx-auto">
            <FaLock className="h-12 w-12 text-primary-500" />
            <h2 className="text-xl font-semibold text-primary">{t('page.forms.results.private')}</h2>
            <p className="text-center text-sm text-primary-700">
              {t('page.forms.results.privateDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalResponses = form.responses.length;

  return (
    <div className="flex flex-col gap-6 items-center">
      <div className="flex flex-col gap-6 w-full px-6">
        {/* Back Button */}
        <Button variant="tertiary" onClick={handleBack}>
          <div className="flex items-center gap-2">
            <FaArrowLeft className="h-4 w-4" />
            {t('general.back')}
          </div>
        </Button>

        {/* Response Count */}
        <div className="w-full max-w-5xl mx-auto overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
          <div className="flex items-center justify-between gap-4 bg-primary-100 px-6 py-2.5">
            <h2 className="text-lg font-semibold text-primary-900">{form.title}</h2>
            <div className="flex items-center gap-2 text-sm text-primary-900">
              <FaChartBar className="h-5 w-5 text-primary" />
              <span className="font-medium">
                {t('page.forms.results.totalResponses', { count: totalResponses })}
              </span>
            </div>
          </div>
          {(form.description || form.deadline) && (
            <div className="px-6 py-4">
              {form.description && <p className="text-sm text-primary-700">{form.description}</p>}
              {form.deadline && (
                <p className={`text-sm text-primary-700 ${form.description ? 'mt-2' : ''}`}>
                  <strong>{t('component.formResponse.deadlineLabel')}:</strong>{' '}
                  <span>{formatDate(form.deadline)}</span>
                </p>
              )}
            </div>
          )}
        </div>

        {totalResponses === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-[12px] bg-secondary-200/70 p-12 w-full max-w-5xl mx-auto">
            <FaUser className="h-12 w-12 text-primary-500" />
            <p className="text-sm text-primary-700">{t('page.forms.results.noResponses')}</p>
          </div>
        ) : (
          <>
            {/* Summary for All Questions */}
            <div className="w-full max-w-5xl mx-auto">
              <h2 className="mb-4 text-lg font-semibold text-primary">
                {t('page.forms.results.summary')}
              </h2>

              <div className="flex flex-col gap-4">
                {form.questions.map((question) => {
                  const qId = question.formQuestionId || question.questionId;
                  const s = questionSummaries[qId];
                  return (
                    <div key={qId} className="rounded-[12px] bg-secondary-200/70 p-4">
                      <h3 className="mb-2 font-medium text-primary">{question.label}</h3>
                      <div className="flex flex-col gap-2">
                      {/* Choice */}
                      {question.type === FormQuestionType.CHOICE &&
                        question.options?.map((option) => {
                          const optId = option.formOptionId || option.id;
                          const count = s?.data?.[optId] || 0;
                          const percentage =
                            totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0;

                          return (
                            <div key={optId} className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-primary">{option.label}</span>
                                  <span className="text-primary-500">
                                    {count} ({percentage}%)
                                  </span>
                                </div>
                                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary-200">
                                  <div
                                    className="h-full rounded-full bg-primary transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      {/* Rating */}
                      {question.type === FormQuestionType.RATING && (
                        <div>
                          <div className="mb-2 text-sm text-primary-700">
                            {t('page.forms.results.average')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.average ? s.data.average.toFixed(1) : '-'}
                            </strong>
                          </div>
                          <div className="flex flex-col gap-1">
                            {Array.from({ length: s?.data?.scale || 5 }, (_, i) => {
                              const value = i + 1;
                              const count = s?.data?.counts?.[value] || 0;
                              const percentage = s?.data?.total
                                ? Math.round((count / s.data.total) * 100)
                                : 0;
                              return (
                                <div key={value} className="flex items-center gap-3">
                                  <div className="w-12 text-sm text-primary">{value}★</div>
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm">
                                      <span />
                                      <span className="text-primary-500">
                                        {count} ({percentage}%)
                                      </span>
                                    </div>
                                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary-200">
                                      <div
                                        className="h-full rounded-full bg-primary"
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Number */}
                      {question.type === FormQuestionType.NUMBER && (
                        <div className="text-sm text-primary-700">
                          <div>
                            {t('page.forms.results.average')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.average?.toFixed(2) ?? '-'}
                            </strong>
                          </div>
                          <div>
                            {t('page.forms.results.min')}:{' '}
                            <strong className="text-primary">{s?.data?.min ?? '-'}</strong>{' '}
                            {t('page.forms.results.max')}:{' '}
                            <strong className="text-primary">{s?.data?.max ?? '-'}</strong>
                          </div>
                        </div>
                      )}

                      {/* Date */}
                      {question.type === FormQuestionType.DATE && (
                        <div className="text-sm text-primary-700">
                          <div>
                            {t('page.forms.results.earliest')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.earliest ? formatDate(s.data.earliest) : '-'}
                            </strong>
                          </div>
                          <div>
                            {t('page.forms.results.latest')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.latest ? formatDate(s.data.latest) : '-'}
                            </strong>
                          </div>
                        </div>
                      )}

                      {/* Text */}
                      {question.type === FormQuestionType.TEXT && (
                        <div className="text-sm text-primary-700">
                          {s?.data?.top?.length ? (
                            <ul className="list-disc pl-5">
                              {s.data.top.map((tItem: any) => (
                                <li key={tItem.text} className="text-primary">
                                  {tItem.text}{' '}
                                  <span className="text-primary-500">({tItem.count})</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      )}
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Individual Responses */}
            <div className="w-full max-w-5xl mx-auto">
              <h2 className="mb-4 text-lg font-semibold text-primary">
                {t('page.forms.results.individualResponses')}
              </h2>
              <div className="flex flex-col gap-4">
                {form.responses.map((response) => {
                  const rId = response.formResponseId || response.responseId || response.id;
                  return (
                    <div key={rId} className="rounded-[12px] bg-secondary-200/70 p-4">
                      <div className="mb-3 flex items-start gap-3">
                      <Avatar
                        src={response.user?.picture}
                        alias={`${response.user?.firstName ?? ''} ${response.user?.lastName ?? ''}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-primary">
                            {response.user?.firstName} {response.user?.lastName}
                          </p>
                          <p className="text-xs text-primary-500">
                            {formatDate(response.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-[52px] flex flex-col gap-2">
                      {form.questions.map((question) => {
                        const qId = question.formQuestionId || question.questionId;
                        return (
                          <div key={qId} className="text-sm">
                            <span className="font-medium text-primary">{question.label}:</span>{' '}
                            <span className="text-primary-700">
                              {getAnswerDisplay(response, qId)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
